import { Route, Routes, useParams, useSearchParams } from 'react-router-dom';
import { TestForm, TestFormInputs } from '../test-form';
import { AgentData, useGetAgent } from '@/features/agents/hooks/use-get-agent';
import {
  DefaultValues,
  Step,
} from '@/features/agents/components/capabilities-studio-widget';
import { useEffect, useState } from 'react';
import { TuneForm, TuneFormInputs } from '../tune-form';
import { DescribeForm, DescribeFormInputs } from '../describe-form';
import { useUiPickerObjects } from '@/hooks/ui/use-ui-picker-objects';

export function EditForm({
  id,
  branch,
  steps,
}: {
  id: string;
  branch: string;
  steps: Step[];
}) {
  const { uuid } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [defaultValues, setDefaultValues] = useState<DefaultValues>({});
  const { data, isLoading } = useGetAgent(uuid!);
  const { data: vaultPickerData } = useUiPickerObjects('vault');

  useEffect(() => {
    if (!data || !vaultPickerData) return;
    const agent = data as AgentData;
    const vaultItems = vaultPickerData?.data?.items || [];
    setDefaultValues({
      test: {
        title: agent.title,
        vaultSchemas: agent.previousVaultMappings
          ? Object.entries(agent.previousVaultMappings).map(([variableName, item]) => {
              const typedItem = item as { uuid: string; container: string; label?: string };
              return {
                variableName,
                uuid: typedItem.uuid,
                container: typedItem.container,
                label: vaultItems.find((v) => v.uuid === typedItem.uuid)?.label ?? typedItem.label,
              };
            })
          : undefined,
      },
      tune: {
        title: agent.title,
      },
      describe: {
        title: agent.title,
        tags: agent.tags,
        description: agent.description,
        documentation: agent.documentation,
        exemplaryInput: agent.exemplaryInput,
        exemplaryOutput: agent.exemplaryOutput,
        inputSchema: agent.inputSchema,
        outputSchema: agent.outputSchema,
        vaultSchemas: agent.vaultSchemas,
      },
    });
  }, [data, vaultPickerData]);

  const runUuidFromUrl = searchParams.get('runUuid');
  const [lastRunUuid, setLastRunUuid] = useState<string | null>(runUuidFromUrl);

  useEffect(() => {
    if (runUuidFromUrl) {
      setLastRunUuid(runUuidFromUrl);
    }
  }, [runUuidFromUrl]);

  // // Remove runUuid from URL after setting the state
  // React.useEffect(() => {
  //   if (runUuidFromUrl) {
  //     const newSearchParams = new URLSearchParams(searchParams);
  //     newSearchParams.delete('runUuid');
  //     setSearchParams(newSearchParams, { replace: true });
  //   }
  // }, [runUuidFromUrl, searchParams, setSearchParams]);

  function handleChange(
    data: TestFormInputs | TuneFormInputs | DescribeFormInputs,
    step: 'test' | 'tune' | 'describe'
  ) {
    setDefaultValues((prev) => ({
      ...prev,
      [step]: {
        ...prev[step],
        ...data,
      },
    }));
  }

  return (
    <Routes>
      <Route
        path="test"
        element={
          <TestForm
            id={id}
            branch={branch}
            defaultValues={defaultValues.test}
            onChange={handleChange}
            steps={steps}
            agent={data}
            isLoading={isLoading}
            onTestRun={setLastRunUuid}
            testRunUuid={lastRunUuid || null}
          />
        }
      />
      <Route
        path="tune"
        element={
          <TuneForm
            id={id}
            branch={branch}
            defaultValues={defaultValues.tune}
            onChange={handleChange}
            steps={steps}
            agent={data}
            isLoading={isLoading}
          />
        }
      />
      <Route
        path="describe"
        element={
          <DescribeForm
            id={id}
            defaultValues={defaultValues.describe}
            onChange={handleChange}
            steps={steps}
            agent={data}
            isLoading={isLoading}
          />
        }
      />
    </Routes>
  );
}
